/**
 * Query hooks, types, tolerant normalizers, filters, and pure derivations for
 * the Cockpit home page sales dashboard.
 *
 * All endpoints are GET under `${API_BASE}/v1/retail/home/` and (except
 * inventory-watch) take `?range=KEY&channels=a,b,c`:
 *
 *   range            resolved fiscal dates for the chosen range key
 *   sales-trend      totals, per-channel split, and a day or hour series
 *   leaderboard      every store with this year, last year, and comp
 *   top-sellers      top items by units vs the same span last year
 *   hourly           average sales per store per hour
 *   wholesale        Hammond's wholesale (Sage invoices); ignores channels
 *   inventory-watch  items running low in stores; ignores both filters
 *
 * The api endpoints ship in parallel with this UI, so every hook uses
 * `retry: false` and every normalizer degrades to `null` on a 404 / 503 /
 * shape mismatch. Tiles render a quiet "Not available yet" state for `null`.
 */

import { useQuery } from '@tanstack/react-query';
import { apiGet } from './api';
import { API_BASE } from './apiBase';

export const HOME_API_BASE = `${API_BASE}/v1/retail/home`;

// ---------------------------------------------------------------------------
// Filters (range + channels), persisted per person in localStorage
// ---------------------------------------------------------------------------

export const RANGE_OPTIONS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last_week', label: 'Last week' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_quarter', label: 'This quarter' },
  { key: 'last_quarter', label: 'Last quarter' },
  { key: 'this_year', label: 'This year' },
] as const;

export type RangeKey = (typeof RANGE_OPTIONS)[number]['key'];

export const CHANNEL_OPTIONS = [
  { key: 'lolli_stores', label: 'Lolli & Pops stores' },
  { key: 'hammonds_store', label: "Hammond's store" },
  { key: 'lolli_online', label: 'Lolli & Pops online' },
  { key: 'hammonds_online', label: "Hammond's online" },
] as const;

export type ChannelKey = (typeof CHANNEL_OPTIONS)[number]['key'];

export type HomeFilters = { range: RangeKey; channels: ChannelKey[] };

export const DEFAULT_RANGE: RangeKey = 'this_month';
export const ALL_CHANNELS: ChannelKey[] = CHANNEL_OPTIONS.map((c) => c.key);
export const FILTERS_STORAGE_KEY = 'cockpit.home.filters';

export function defaultFilters(): HomeFilters {
  return { range: DEFAULT_RANGE, channels: [...ALL_CHANNELS] };
}

function isRangeKey(v: unknown): v is RangeKey {
  return RANGE_OPTIONS.some((r) => r.key === v);
}

/**
 * Turn any stored value (a JSON string or an object) into valid filters.
 * Unknown range falls back to the default, unknown channels are dropped,
 * channels come back in canonical order, and an empty channel list falls back
 * to all four (at least one stays on).
 */
export function parseFilters(raw: unknown): HomeFilters {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return defaultFilters();
    }
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return defaultFilters();
  }
  const v = value as Record<string, unknown>;
  const range = isRangeKey(v.range) ? v.range : DEFAULT_RANGE;
  const picked = Array.isArray(v.channels) ? v.channels : [];
  const channels = ALL_CHANNELS.filter((c) => picked.includes(c));
  return { range, channels: channels.length > 0 ? channels : [...ALL_CHANNELS] };
}

/** Query string for the api, e.g. `range=this_month&channels=lolli_stores,lolli_online`. */
export function filtersQuery(f: HomeFilters): string {
  const channels = ALL_CHANNELS.filter((c) => f.channels.includes(c));
  return `range=${f.range}&channels=${(channels.length > 0 ? channels : ALL_CHANNELS).join(',')}`;
}

/** Flip one channel. The last channel left on cannot be turned off. */
export function toggleChannel(f: HomeFilters, channel: ChannelKey): HomeFilters {
  const on = f.channels.includes(channel);
  if (on && f.channels.length === 1) return f;
  const next = on ? f.channels.filter((c) => c !== channel) : [...f.channels, channel];
  return { ...f, channels: ALL_CHANNELS.filter((c) => next.includes(c)) };
}

export function readStoredFilters(): HomeFilters {
  try {
    return parseFilters(window.localStorage.getItem(FILTERS_STORAGE_KEY));
  } catch {
    return defaultFilters();
  }
}

export function writeStoredFilters(f: HomeFilters): void {
  try {
    window.localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(f));
  } catch {
    /* storage blocked or full: the choice just does not persist */
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RangeInfo = {
  key: string;
  label: string;
  start: string | null;
  end: string | null;
  ly_start: string | null;
  ly_end: string | null;
  data_through: string | null;
  /** 'HH:00' when today is a partial day (last year cut off at the same hour), else null. */
  partial_through: string | null;
};

export type TrendPointRaw = { t: string; ty: number; ly: number | null };

export type ChannelTotal = { channel: string; label: string; ty: number; ly: number | null };

export type SalesTrend = {
  range: RangeInfo;
  total_ty: number;
  total_ly: number | null;
  by_channel: ChannelTotal[];
  grain: 'day' | 'hour';
  points: TrendPointRaw[];
};

export type LeaderStore = {
  store_no: string;
  store_name: string;
  ty: number | null;
  ly: number | null;
  comp: number | null;
};

export type Leaderboard = { range: RangeInfo; stores: LeaderStore[]; note: string | null };

export type TopSeller = {
  item_no: string;
  descr: string | null;
  units: number;
  units_ly: number | null;
  net_sales: number;
};

export type TopSellers = { range: RangeInfo; items: TopSeller[] };

export type HourlyCell = { hour: number; avg_sales: number };
export type HourlyStore = {
  store_no: string;
  store_name: string;
  total: number;
  cells: HourlyCell[];
};
export type HourlyHeatmap = {
  range: RangeInfo;
  hours: number[];
  stores: HourlyStore[];
  note: string | null;
};

export type Wholesale = {
  range: RangeInfo;
  total_ty: number;
  total_ly: number | null;
  invoices_ty: number;
  invoices_ly: number | null;
  avg_invoice_ty: number | null;
  avg_invoice_ly: number | null;
  /** Set when last year is not comparable; the LY figures and points are then null. */
  ly_note: string | null;
  points: TrendPointRaw[];
};

export type InventoryWatchItem = {
  item_no: string;
  descr: string | null;
  stores_low: number;
  stores_carrying: number;
  on_hand: number;
  weekly_units: number;
  weeks_left: number | null;
};
export type InventoryWatch = { as_of: string | null; items: InventoryWatchItem[] };

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Tolerant normalizers (return null when the payload is not usable)
// ---------------------------------------------------------------------------

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

export function normalizeRange(raw: unknown): RangeInfo | null {
  if (!isObj(raw)) return null;
  const key = str(raw.key);
  const label = str(raw.label);
  if (!key || !label) return null;
  return {
    key,
    label,
    start: str(raw.start),
    end: str(raw.end),
    ly_start: str(raw.ly_start),
    ly_end: str(raw.ly_end),
    data_through: str(raw.data_through),
    partial_through: str(raw.partial_through),
  };
}

/** `{ range }` response used by the filter bar. */
export function normalizeRangeResponse(raw: unknown): RangeInfo | null {
  return isObj(raw) ? normalizeRange(raw.range) : null;
}

function normalizePoints(raw: unknown): TrendPointRaw[] | null {
  if (!Array.isArray(raw)) return null;
  const out: TrendPointRaw[] = [];
  for (const p of raw) {
    if (!isObj(p)) continue;
    const t = str(p.t);
    if (!t) continue;
    out.push({ t, ty: num(p.ty) ?? 0, ly: num(p.ly) });
  }
  return out;
}

export function normalizeSalesTrend(raw: unknown): SalesTrend | null {
  if (!isObj(raw)) return null;
  const range = normalizeRange(raw.range);
  const points = normalizePoints(raw.points);
  if (!range || !points) return null;
  const by_channel: ChannelTotal[] = [];
  if (Array.isArray(raw.by_channel)) {
    for (const c of raw.by_channel) {
      if (!isObj(c)) continue;
      const channel = str(c.channel);
      if (!channel) continue;
      by_channel.push({ channel, label: str(c.label) ?? channel, ty: num(c.ty) ?? 0, ly: num(c.ly) });
    }
  }
  return {
    range,
    total_ty: num(raw.total_ty) ?? 0,
    total_ly: num(raw.total_ly),
    by_channel,
    grain: raw.grain === 'hour' ? 'hour' : 'day',
    points,
  };
}

export function normalizeLeaderboard(raw: unknown): Leaderboard | null {
  if (!isObj(raw) || !Array.isArray(raw.stores)) return null;
  const range = normalizeRange(raw.range);
  if (!range) return null;
  const stores: LeaderStore[] = [];
  for (const s of raw.stores) {
    if (!isObj(s)) continue;
    const store_no = str(s.store_no);
    if (!store_no) continue;
    stores.push({
      store_no,
      store_name: str(s.store_name) ?? store_no,
      ty: num(s.ty),
      ly: num(s.ly),
      comp: num(s.comp),
    });
  }
  return { range, stores, note: str(raw.note) };
}

export function normalizeTopSellers(raw: unknown): TopSellers | null {
  if (!isObj(raw) || !Array.isArray(raw.items)) return null;
  const range = normalizeRange(raw.range);
  if (!range) return null;
  const items: TopSeller[] = [];
  for (const i of raw.items) {
    if (!isObj(i)) continue;
    const item_no = str(i.item_no);
    if (!item_no) continue;
    items.push({
      item_no,
      descr: str(i.descr),
      units: num(i.units) ?? 0,
      units_ly: num(i.units_ly),
      net_sales: num(i.net_sales) ?? 0,
    });
  }
  return { range, items };
}

export function normalizeHourly(raw: unknown): HourlyHeatmap | null {
  if (!isObj(raw) || !Array.isArray(raw.hours) || !Array.isArray(raw.stores)) return null;
  const range = normalizeRange(raw.range);
  if (!range) return null;
  const hours = raw.hours.map(num).filter((h): h is number => h !== null);
  const stores: HourlyStore[] = [];
  for (const s of raw.stores) {
    if (!isObj(s)) continue;
    const store_no = str(s.store_no);
    if (!store_no || !Array.isArray(s.cells)) continue;
    const cells: HourlyCell[] = [];
    for (const c of s.cells) {
      if (!isObj(c)) continue;
      const hour = num(c.hour);
      if (hour === null) continue;
      cells.push({ hour, avg_sales: num(c.avg_sales) ?? 0 });
    }
    stores.push({
      store_no,
      store_name: str(s.store_name) ?? store_no,
      total: num(s.total) ?? cells.reduce((a, c) => a + c.avg_sales, 0),
      cells,
    });
  }
  return { range, hours, stores, note: str(raw.note) };
}

export function normalizeWholesale(raw: unknown): Wholesale | null {
  if (!isObj(raw)) return null;
  const range = normalizeRange(raw.range);
  const points = normalizePoints(raw.points);
  if (!range || !points) return null;
  return {
    range,
    total_ty: num(raw.total_ty) ?? 0,
    total_ly: num(raw.total_ly),
    invoices_ty: num(raw.invoices_ty) ?? 0,
    invoices_ly: num(raw.invoices_ly),
    avg_invoice_ty: num(raw.avg_invoice_ty),
    avg_invoice_ly: num(raw.avg_invoice_ly),
    ly_note: str(raw.ly_note),
    points,
  };
}

export function normalizeInventoryWatch(raw: unknown): InventoryWatch | null {
  if (!isObj(raw) || !Array.isArray(raw.items)) return null;
  const items: InventoryWatchItem[] = [];
  for (const i of raw.items) {
    if (!isObj(i)) continue;
    const item_no = str(i.item_no);
    if (!item_no) continue;
    items.push({
      item_no,
      descr: str(i.descr),
      stores_low: num(i.stores_low) ?? 0,
      stores_carrying: num(i.stores_carrying) ?? 0,
      on_hand: num(i.on_hand) ?? 0,
      weekly_units: num(i.weekly_units) ?? 0,
      weeks_left: num(i.weeks_left),
    });
  }
  return { as_of: str(raw.as_of), items };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export type HomeTile<T> = {
  data: T | null;
  isLoading: boolean;
  /** True when the endpoint is missing, errored, or returned an unusable shape. */
  unavailable: boolean;
};

/** `filters` null means the endpoint ignores both filters (inventory watch). */
function useHomeTile<T>(
  key: string,
  filters: HomeFilters | null,
  normalize: (raw: unknown) => T | null,
): HomeTile<T> {
  const qs = filters ? filtersQuery(filters) : '';
  const query = useQuery<T | null, Error>({
    queryKey: ['home-dashboard', key, qs],
    queryFn: async () =>
      normalize(await apiGet<unknown>(`${HOME_API_BASE}/${key}${qs ? `?${qs}` : ''}`)),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    unavailable: !query.isLoading && (!!query.error || query.data == null),
  };
}

export const useHomeRange = (f: HomeFilters) => useHomeTile('range', f, normalizeRangeResponse);
export const useHomeSalesTrend = (f: HomeFilters) => useHomeTile('sales-trend', f, normalizeSalesTrend);
export const useHomeLeaderboard = (f: HomeFilters) => useHomeTile('leaderboard', f, normalizeLeaderboard);
export const useHomeTopSellers = (f: HomeFilters) => useHomeTile('top-sellers', f, normalizeTopSellers);
export const useHomeHourly = (f: HomeFilters) => useHomeTile('hourly', f, normalizeHourly);
export const useHomeWholesale = (f: HomeFilters) => useHomeTile('wholesale', f, normalizeWholesale);
export const useHomeInventoryWatch = () =>
  useHomeTile('inventory-watch', null, normalizeInventoryWatch);

// ---------------------------------------------------------------------------
// Sales vs. last year
// ---------------------------------------------------------------------------

/** Ratio change ty vs ly (0.05 = +5%). Null when ly is missing or not positive. */
export function deltaRatio(ty: number, ly: number | null): number | null {
  if (ly == null || !(ly > 0)) return null;
  return ty / ly - 1;
}

export type TrendPoint = {
  t: string;
  ty: number;
  ly: number | null;
  /** Ratio change vs last year, null when not comparable. */
  pct: number | null;
};

/** Chart series with the ratio change attached to each point. */
export function buildSeries(points: TrendPointRaw[] | null | undefined): TrendPoint[] {
  return (points ?? []).map((p) => ({ ...p, pct: deltaRatio(p.ty, p.ly) }));
}

/** X-axis label: "9a" for an hourly point, "Sep 30" for a daily one. */
export function formatPointLabel(t: string, grain: 'day' | 'hour'): string {
  if (grain === 'hour') {
    const m = /T(\d{2})/.exec(t);
    return m ? formatHour(Number(m[1])) : t;
  }
  return formatShortDate(t);
}

/** Channels that are both returned and currently selected, in the returned order. */
export function selectedChannelRows(by: ChannelTotal[], filters: HomeFilters): ChannelTotal[] {
  return by.filter((c) => (filters.channels as string[]).includes(c.channel));
}

/** Y-axis tick: 12500 -> "$12.5k", 800 -> "$800", 0 -> "$0". */
export function formatAxisK(v: number): string {
  if (!Number.isFinite(v)) return '';
  if (Math.abs(v) >= 1000) {
    const k = v / 1000;
    return `$${Number.isInteger(k) ? k : k.toFixed(1)}k`;
  }
  return `$${Math.round(v)}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-09-30" -> "Sep 30" (no timezone shift). */
export function formatShortDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const mon = MONTHS[Number(m[2]) - 1];
  return mon ? `${mon} ${Number(m[3])}` : iso;
}

// ---------------------------------------------------------------------------
// Store leaderboard
// ---------------------------------------------------------------------------

export type LeaderRow = LeaderStore;

/**
 * "Growing fastest" is the top `n` by comp, "Needs a look" the bottom `n` of
 * the rest. Stores without a comp are left out of both lists.
 */
export function deriveLeaderboard(
  stores: LeaderStore[],
  n = 5,
): { growing: LeaderRow[]; needsLook: LeaderRow[] } {
  const rows = (stores ?? []).filter((s) => s.comp != null && Number.isFinite(s.comp));
  const growing = [...rows].sort((a, b) => (b.comp ?? 0) - (a.comp ?? 0)).slice(0, n);
  const taken = new Set(growing.map((r) => r.store_no));
  const needsLook = [...rows]
    .sort((a, b) => (a.comp ?? 0) - (b.comp ?? 0))
    .filter((r) => !taken.has(r.store_no))
    .slice(0, n);
  return { growing, needsLook };
}

/** Every store sorted by comp, highest first; stores without a comp go last. */
export function sortAllStores(stores: LeaderStore[]): LeaderRow[] {
  return [...(stores ?? [])].sort((a, b) => {
    if (a.comp == null && b.comp == null) return a.store_name.localeCompare(b.store_name);
    if (a.comp == null) return 1;
    if (b.comp == null) return -1;
    return b.comp - a.comp;
  });
}

// ---------------------------------------------------------------------------
// Top sellers
// ---------------------------------------------------------------------------

export type SellerTrend = {
  direction: 'up' | 'down' | 'flat';
  pct: number | null;
  /** True when there were no sales in the same span last year. */
  isNew: boolean;
};

/** Unit trend vs the same span last year. Within 1% either way is "flat". */
export function sellerTrend(units: number, unitsLy: number | null): SellerTrend {
  if (unitsLy == null || !(unitsLy > 0)) {
    return units > 0
      ? { direction: 'up', pct: null, isNew: true }
      : { direction: 'flat', pct: null, isNew: false };
  }
  const pct = units / unitsLy - 1;
  if (Math.abs(pct) <= 0.01) return { direction: 'flat', pct, isNew: false };
  return { direction: pct > 0 ? 'up' : 'down', pct, isNew: false };
}

const UNITS_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/** Whole-number units with thousands separators: 1234.4 -> "1,234". */
export function formatUnits(n: number): string {
  return UNITS_FORMAT.format(Number.isFinite(n) ? n : 0);
}

/** "Through Oct 5" when the data stops before the range ends, else null. */
export function dataThroughNote(
  range: Pick<RangeInfo, 'end' | 'data_through'> | null,
): string | null {
  if (!range?.data_through || !range.end) return null;
  return range.data_through < range.end ? `Through ${formatShortDate(range.data_through)}` : null;
}

// ---------------------------------------------------------------------------
// Heatmap
// ---------------------------------------------------------------------------

/** 0 -> "12a", 9 -> "9a", 12 -> "12p", 15 -> "3p". */
export function formatHour(h: number): string {
  const hh = ((Math.round(h) % 24) + 24) % 24;
  const suffix = hh < 12 ? 'a' : 'p';
  const twelve = hh % 12 === 0 ? 12 : hh % 12;
  return `${twelve}${suffix}`;
}

/** 'HH:00' (24-hour) -> "2 PM". Null for anything that is not a valid hour. */
export function formatPartialHour(partial: string | null | undefined): string | null {
  if (!partial) return null;
  const m = /^(\d{1,2}):\d{2}$/.exec(partial.trim());
  if (!m) return null;
  const h = Number(m[1]);
  if (h > 23) return null;
  return `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? 'AM' : 'PM'}`;
}

/** Filter bar text, e.g. "Today through 2 PM; last year cut off at the same hour". */
export function partialRangeNote(
  range: Pick<RangeInfo, 'partial_through'> | null | undefined,
): string | null {
  const hour = formatPartialHour(range?.partial_through);
  return hour ? `Today through ${hour}; last year cut off at the same hour` : null;
}

/** Sales vs. last year caption suffix, e.g. "(today through 2 PM)". */
export function partialCaptionSuffix(
  range: Pick<RangeInfo, 'partial_through'> | null | undefined,
): string | null {
  const hour = formatPartialHour(range?.partial_through);
  return hour ? `(today through ${hour})` : null;
}

/** What the wholesale tile shows for last year. A ly_note or null total means no delta. */
export function wholesaleLyView(w: Pick<Wholesale, 'total_ty' | 'total_ly' | 'ly_note'>): {
  delta: number | null;
  showLyChart: boolean;
  note: string | null;
} {
  const comparable = !w.ly_note && w.total_ly != null;
  return {
    delta: comparable ? deltaRatio(w.total_ty, w.total_ly) : null,
    showLyChart: comparable,
    note: w.ly_note,
  };
}

export const HEAT_STEPS = 7;

/** Map a value onto 0..6 against `max`. Zero (or no max) is the lightest step. */
export function heatLevel(value: number, max: number): number {
  if (!(value > 0) || !(max > 0)) return 0;
  return Math.min(HEAT_STEPS - 1, Math.floor((value / max) * HEAT_STEPS));
}

export function heatMax(stores: HourlyStore[]): number {
  let max = 0;
  for (const s of stores) for (const c of s.cells) if (c.avg_sales > max) max = c.avg_sales;
  return max;
}

// ---------------------------------------------------------------------------
// Inventory watch
// ---------------------------------------------------------------------------

export type StockStatus = { label: 'Reorder now' | 'Watch'; critical: boolean };

export function stockStatus(weeksLeft: number | null): StockStatus {
  if (weeksLeft != null && weeksLeft < 1) return { label: 'Reorder now', critical: true };
  return { label: 'Watch', critical: false };
}

/** Fill fraction for the weeks-left bar: 0 weeks empty, 2+ weeks full. */
export function weeksBarFraction(weeksLeft: number | null): number {
  if (weeksLeft == null || !(weeksLeft > 0)) return 0;
  return Math.min(1, weeksLeft / 2);
}

/** Most urgent first; unknown weeks-left last. */
export function sortInventoryWatch(items: InventoryWatchItem[]): InventoryWatchItem[] {
  return [...items].sort((a, b) => {
    const aw = a.weeks_left ?? Number.POSITIVE_INFINITY;
    const bw = b.weeks_left ?? Number.POSITIVE_INFINITY;
    return aw - bw;
  });
}
